import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment, Button, Message } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import {
  updateRezeptgruppe,
  getRezeptgruppeByID,
  getRezeptgruppen,
  deleteRezeptgruppe,
  getVistPermissions,
} from '../../../actions';
import { selectIsAdmin } from '../../../helpers/permissions';

import ButtonArea from '../ButtonArea';

import RezeptgruppeForm from './RezeptgruppeForm';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const RezeptgruppeEdit = ({
  pathname,
  rezeptgruppe,
  rezeptgruppen,
  getRezeptgruppen,
  getRezeptgruppeByID,
  updateRezeptgruppe,
  deleteRezeptgruppe,
  intl,
  token,
  permissions = null,
  isAdmin,
  getVistPermissions,
}) => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
  });

  const [error, setError] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [isRezeptgruppeNameDuplicate, setIsRezeptgruppeNameDuplicate] =
    useState(false);
  const isSubmitDisabled = formData.name === '';

  const history = useHistory();
  // Rezeptgruppe edit is admin-only today; Step 11 will disable
  // the screen entirely after Zeelandia's manual cleanup.
  // flag to make sure permissions are fully loaded before returning
  // an unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Fetch @vist_permissions if the reducer hasn't seen a response yet.
    if (!permissions && permissionsLoading) {
      getVistPermissions()
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch(() => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setIsClient(true);
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');
    getRezeptgruppen({ showAll: true });
    getRezeptgruppeByID(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRezeptgruppeByID, getRezeptgruppen]);

  useEffect(() => {
    if (rezeptgruppe) {
      setFormData({
        name: rezeptgruppe.name,
      });
    }
  }, [rezeptgruppe]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));

    if (name === 'name' && value !== rezeptgruppe.name) {
      const isDuplicate = rezeptgruppen.some(
        (rezeptgruppe) => rezeptgruppe.name === value,
      );
      setIsRezeptgruppeNameDuplicate(isDuplicate);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    try {
      await updateRezeptgruppe(id, formData);
      history.push(`/controlpanel/rezeptdatenbank/rezeptgruppen`);
      // Handle success or show a success message
    } catch (error) {
      // Handle error during update
      setError(error);
    }
  };

  const handleDelete = async () => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    try {
      await deleteRezeptgruppe(id);
      setDeleteSuccess(true);
    } catch (error) {
      // Handle delete error
      setError(error);
    }
  };

  if (!token) {
    return <Error error={{ status: 401 }} />;
  }
  if (!permissionsLoading && !isAdmin) {
    return <Error error={{ status: 401 }} />;
  }
  return (
    <div className="view-wrapper">
      <Helmet title="Rezeptgruppe Bearbeiten" />
      <Container className="controlpanel">
        <Segment.Group raised>
          <Segment className="primary">
            <div className="rezeptdatenbank header">
              <div className="header-container">
                <h1>{rezeptgruppe && rezeptgruppe.name} </h1>
              </div>
              <div className="button-container">
                <ButtonArea
                  handleSubmit={handleSubmit}
                  handleDelete={handleDelete}
                  isSubmitDisabled={isSubmitDisabled}
                  backURL={'/controlpanel/rezeptdatenbank/rezeptgruppen'}
                  typeString={'die Rezeptgruppe'}
                />
              </div>
            </div>
            <div className="rezeptdatenbank header"></div>
          </Segment>
          <Segment className="rdbAddEditForm">
            {error && (
              <Message negative>
                <Message.Header>Error</Message.Header>
                <p>{error.message}</p>
              </Message>
            )}
            {deleteSuccess ? (
              <Message positive>
                <Message.Header>Rezeptgruppe wurde gelöscht</Message.Header>
                <Button
                  primary
                  as={Link}
                  to="/controlpanel/rezeptdatenbank/rezeptgruppen"
                >
                  Zurück zur Rezeptgruppen-Übersicht
                </Button>
              </Message>
            ) : (
              <>
                <div className="rdb edit form rezeptgruppen">
                  <RezeptgruppeForm
                    formData={formData}
                    isRezeptgruppeNameDuplicate={isRezeptgruppeNameDuplicate}
                    handleInputChange={handleInputChange}
                  />
                </div>
              </>
            )}
          </Segment>
        </Segment.Group>
      </Container>
      {isClient && (
        <Portal node={document.getElementById('toolbar')}>
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/controlpanel/rezeptgruppedatenbank" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />
        </Portal>
      )}
    </div>
  );
};

RezeptgruppeEdit.propTypes = {
  pathname: PropTypes.string.isRequired,
  rezeptgruppen: PropTypes.array.isRequired,
  rezeptgruppe: PropTypes.object,
  getRezeptgruppeByID: PropTypes.func.isRequired,
  updateRezeptgruppe: PropTypes.func.isRequired,
  deleteRezeptgruppe: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  token: PropTypes.string,
  permissions: PropTypes.object,
  isAdmin: PropTypes.bool,
  getVistPermissions: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      pathname: state.router.location.pathname,
      rezeptgruppe: state.rezeptgruppeByID.data,
      rezeptgruppen: state.rezeptgruppen.data.rezeptgruppen,
      token: state.userSession.token,
      permissions: state.permissions.permissions,
      isAdmin: selectIsAdmin(state),
    }),
    {
      updateRezeptgruppe,
      getRezeptgruppeByID,
      deleteRezeptgruppe,
      getRezeptgruppen,
      getVistPermissions,
    },
  ),
)(RezeptgruppeEdit);
